import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface DescriptorFrequency {
  descriptor: string;
  count: number;
  example_id: string;
}

function computeFrequency(
  rows: Array<{ id: string; values: string[] }>
): DescriptorFrequency[] {
  const freq = new Map<string, { count: number; example_id: string }>();
  for (const row of rows) {
    for (const v of row.values) {
      if (!v) continue;
      const existing = freq.get(v);
      if (existing) {
        existing.count++;
      } else {
        freq.set(v, { count: 1, example_id: row.id });
      }
    }
  }
  return [...freq.entries()]
    .map(([descriptor, { count, example_id }]) => ({
      descriptor,
      count,
      example_id,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 50);
}

export function useTopDescriptors(scope: "memory" | "photo", days: number) {
  const query = useQuery({
    queryKey: ["top-descriptors", scope, days],
    queryFn: async (): Promise<DescriptorFrequency[]> => {
      const since = new Date(
        Date.now() - days * 24 * 60 * 60 * 1000
      ).toISOString();

      if (scope === "memory") {
        const { data, error } = await supabase
          .from("memories")
          .select("id, descriptors")
          .gte("created_at", since);
        if (error) throw error;
        return computeFrequency(
          (data ?? []).map((r) => ({
            id: r.id,
            values: r.descriptors ?? [],
          }))
        );
      }

      // photo scope: fetch memory IDs from date range, then artefacts for those IDs
      const { data: memData, error: memError } = await supabase
        .from("memories")
        .select("id")
        .gte("created_at", since);
      if (memError) throw memError;

      const memoryIds = (memData ?? []).map((m) => m.id);
      if (memoryIds.length === 0) return [];

      const { data: artData, error: artError } = await supabase
        .from("memory_artefacts")
        .select("memory_id, scene_descriptors")
        .in("memory_id", memoryIds);
      if (artError) throw artError;

      return computeFrequency(
        (artData ?? []).map((r) => ({
          id: r.memory_id,
          values: r.scene_descriptors ?? [],
        }))
      );
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
